import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { PageHead } from '../components/Notebook';
import AssistantMemory from '../agent/AssistantMemory';
import { getDensity, setDensity } from '../utils/density';
import { getTodayLayout, setTodayLayout } from '../utils/todayLayout';
import { useSubscription } from '../context/SubscriptionContext';
import { getColors, formatDate } from '../theme';
import { devLog, devError } from '../utils/debugLogger';
import { SettingsPageSkeleton } from '../components/LoadingSkeleton';
import {
  Mail,
  Lock,
  CreditCard,
  Trash2,
  Check,
  X,
  Crown,
  Sparkles,
  CheckSquare,
  Target,
  AlertTriangle,
  ExternalLink,
  Shield,
  Loader,
  RefreshCw,
  Settings,
  ChevronRight,
  Heart,
  LogOut,
  Clock,
  Trophy,
  Flame,
  Star,
  Award,
  Zap,
  TrendingUp,
  ClipboardList,
  Sun,
  Moon,
  Monitor,
  Sunset,
  Download,
} from 'lucide-react';
import { auth, db } from '../firebase';
import { 
  updateProfile, 
  updatePassword, 
  EmailAuthProvider, 
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  deleteUser,
  signOut 
} from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { getAllTiers } from '../config/tiers';
import { FUNCTION_URLS } from '../config/functions';
import { DevBadge } from '../components/LockedFeature';
import { redirectToPortal, syncSubscriptionStatus } from '../services/stripe';
import CheckoutModal from '../components/CheckoutModal';
import Logo from '../components/Logo';
import { Link } from 'react-router-dom';

// Get plans from tiers config (excluding dev tier from public display)
const PLANS = getAllTiers(false).map(tier => ({
  id: tier.id,
  name: tier.name,
  price: tier.monthlyPrice,
  monthlyPrice: tier.monthlyPrice,
  yearlyPrice: tier.yearlyPrice,
  period: tier.monthlyPrice === 0 ? '' : '/month',
  description: tier.description,
  features: tier.featureList,
  cta: tier.id === 'free' ? 'Current Plan' : 'Upgrade to Pro',
  popular: tier.id === 'pro',
}));

// Achievement definitions
const ACHIEVEMENTS = [
  {
    id: 'first_habit',
    name: 'First Steps',
    description: 'Complete your first habit',
    icon: Star,
    check: (data) => data.totalCompletions >= 1,
  },
  {
    id: 'week_streak',
    name: 'Week Warrior',
    description: 'Maintain a 7-day streak',
    icon: Flame,
    check: (data) => data.maxStreak >= 7,
  },
  {
    id: 'perfect_day',
    name: 'Perfect Day',
    description: 'Complete all habits in a day',
    icon: CheckSquare,
    check: (data) => data.hadPerfectDay,
  },
  {
    id: 'task_master',
    name: 'Task Master',
    description: 'Complete 10 tasks',
    icon: ClipboardList,
    check: (data) => data.completedTasks >= 10,
  },
  {
    id: 'goal_crusher',
    name: 'Goal Crusher',
    description: 'Complete your first goal',
    icon: Target,
    check: (data) => data.completedGoals >= 1,
  },
  {
    id: 'month_streak',
    name: 'Monthly Master',
    description: '30-day habit streak',
    icon: Crown,
    check: (data) => data.maxStreak >= 30,
  },
  {
    id: 'consistency_king',
    name: 'Consistency King',
    description: '80%+ monthly completion',
    icon: TrendingUp,
    check: (data) => data.monthlyCompletionRate >= 80,
  },
  {
    id: 'early_bird',
    name: 'Early Bird',
    description: '50 morning habits done',
    icon: Zap,
    check: (data) => data.morningCompletions >= 50,
  },
  {
    id: 'centurion',
    name: 'Centurion',
    description: '100 habits completed',
    icon: Award,
    check: (data) => data.totalCompletions >= 100,
  },
  {
    id: 'legendary',
    name: 'Legendary',
    description: '100-day streak',
    icon: Sparkles,
    check: (data) => data.maxStreak >= 100,
  },
];

const SettingsPage = () => {
  const { isDark, themeMode, toggleTheme, changeThemeMode, user, habits, completions, tasks, archivedTasks, goals, archivedGoals, events, calendars, dataLoading } = useApp();
  const { 
    tier, isPro, isDev, isFriend, tierConfig, subscriptionData, hasFeature,
    iOSOfferings, purchaseIOSSubscription, restoreIOSPurchases,
    isNativeApp, showCustomerCenter, showPaywall,
  } = useSubscription();
  const navigate = useNavigate();
  const colors = getColors(isDark);
  const [density, setDensityState] = useState(getDensity);
  const [todayLayout, setTodayLayoutState] = useState(getTodayLayout);
  
  // Profile state
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState(false);
  
  // Password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  
  // Subscription state
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'yearly'
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutPriceType, setCheckoutPriceType] = useState('monthly');
  const currentPlan = tier; // Use actual tier from SubscriptionContext
  
  // Notification settings state
  const [dailyReminderEnabled, setDailyReminderEnabled] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  
  // Export data state
  const [exporting, setExporting] = useState(false);

  // Check if user has analytics access (Pro feature)
  const hasAnalyticsAccess = hasFeature('analytics');

  // Achievement data calculation
  const achievementData = useMemo(() => {
    // Total completions
    let totalCompletions = 0;
    let morningCompletions = 0;
    let maxStreak = 0;
    let hadPerfectDay = false;

    // Check each date for perfect days and count completions
    const allDates = new Set();
    (habits || []).forEach(habit => {
      if (completions?.[habit.id]) {
        Object.keys(completions[habit.id]).forEach(date => {
          if (completions[habit.id][date]) {
            totalCompletions++;
            allDates.add(date);

            // Count morning habits
            if (habit.timeOfDay === 'morning') {
              morningCompletions++;
            }
          }
        });
      }

      // Track max streak
      if (habit.longestStreak > maxStreak) {
        maxStreak = habit.longestStreak;
      }
      if (habit.currentStreak > maxStreak) {
        maxStreak = habit.currentStreak;
      }
    });

    // Check for perfect days
    allDates.forEach(dateStr => {
      let completedOnDate = 0;
      (habits || []).forEach(habit => {
        if (completions?.[habit.id]?.[dateStr]) {
          completedOnDate++;
        }
      });
      if (completedOnDate === habits?.length && habits?.length > 0) {
        hadPerfectDay = true;
      }
    });

    // Monthly completion rate (current month)
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    let monthCompletions = 0;
    let monthTotal = 0;
    const checkDate = new Date(monthStart);
    while (checkDate <= today) {
      const checkStr = formatDate(checkDate);
      (habits || []).forEach(habit => {
        monthTotal++;
        if (completions?.[habit.id]?.[checkStr]) {
          monthCompletions++;
        }
      });
      checkDate.setDate(checkDate.getDate() + 1);
    }
    const monthlyCompletionRate = monthTotal > 0 ? (monthCompletions / monthTotal) * 100 : 0;

    // Completed tasks and goals
    const completedTasks = (archivedTasks || []).length;
    const completedGoals = (archivedGoals || []).length;

    return {
      totalCompletions,
      morningCompletions,
      maxStreak,
      hadPerfectDay,
      completedTasks,
      completedGoals,
      monthlyCompletionRate,
    };
  }, [habits, completions, archivedTasks, archivedGoals]);

  // Calculate unlocked achievements
  const unlockedAchievements = useMemo(() => {
    return ACHIEVEMENTS.filter(a => a.check(achievementData));
  }, [achievementData]);

  // Check URL params for checkout success/cancel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      // Payment successful - the webhook will update Firestore
      // Show a success message
      alert('Welcome to Pro. Your subscription is now active.');
      // Clean up URL
      window.history.replaceState({}, '', '/settings');
    }
    if (urlParams.get('canceled') === 'true') {
      // User canceled checkout
      window.history.replaceState({}, '', '/settings');
    }
  }, []);
  
  // Load notification settings from Firestore
  useEffect(() => {
    const loadNotificationSettings = async () => {
      if (!user?.userId) return;
      try {
        const { getDoc } = await import('firebase/firestore');
        const userDoc = await getDoc(doc(db, 'users', user.userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setDailyReminderEnabled(data.dailyReminderEnabled || false);
        }
      } catch (error) {
        console.error('Failed to load notification settings:', error);
      }
    };
    loadNotificationSettings();
  }, [user?.userId]);
  
  // Handle daily reminder toggle
  const handleDailyReminderToggle = async () => {
    if (!user?.userId) {
      console.log('No user userId, returning');
      return;
    }
    
    const newValue = !dailyReminderEnabled;
    
    // Update UI immediately (optimistic update)
    setDailyReminderEnabled(newValue);
    setSavingNotifications(true);
    
    try {
      const { setDoc } = await import('firebase/firestore');
      
      // Get user's timezone offset in hours
      const timezoneOffset = -(new Date().getTimezoneOffset() / 60);
      
      await setDoc(doc(db, 'users', user.userId), {
        dailyReminderEnabled: newValue,
        timezoneOffset: timezoneOffset,
        emailTheme: isDark ? 'dark' : 'light',
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      // Revert on error
      setDailyReminderEnabled(!newValue);
    } finally {
      setSavingNotifications(false);
    }
  };
  
  // Send test email
  const handleTestEmail = async () => {
    if (!user) return;
    
    setTestingEmail(true);
    setEmailTestResult(null);
    
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        FUNCTION_URLS.notifications.testDailyDigest,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      const result = await response.json();
      setEmailTestResult(result);
    } catch (error) {
      setEmailTestResult({ success: false, message: error.message });
    } finally {
      setTestingEmail(false);
    }
  };
  
  // Check if user is using email/password auth (not OAuth)
  const isEmailPasswordUser = auth.currentUser?.providerData?.[0]?.providerId === 'password';
  
  // Handle name update
  const handleUpdateName = async () => {
    if (!displayName.trim()) {
      setNameError('Please enter a name');
      return;
    }
    
    setSavingName(true);
    setNameError('');
    
    try {
      await updateProfile(auth.currentUser, { displayName: displayName.trim() });
      setNameSuccess(true);
      setEditingName(false);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch (error) {
      setNameError(error.message);
    } finally {
      setSavingName(false);
    }
  };
  
  // Handle password update
  const handleUpdatePassword = async () => {
    setPasswordError('');
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setSavingPassword(true);
    
    try {
      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      
      // Update password
      await updatePassword(auth.currentUser, newPassword);
      
      setPasswordSuccess(true);
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else {
        setPasswordError(error.message);
      }
    } finally {
      setSavingPassword(false);
    }
  };
  
  // Handle export user data
  const handleExportData = async () => {
    setExporting(true);
    try {
      const exportData = {
        exportedAt: new Date().toISOString(),
        user: {
          email: user?.email,
          displayName: user?.displayName,
        },
        habits: habits || [],
        completions: completions || {},
        tasks: tasks || [],
        archivedTasks: archivedTasks || [],
        goals: goals || [],
        archivedGoals: archivedGoals || [],
        events: events || [],
        calendars: calendars || [],
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mind-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };
  
  // Handle account deletion - validate first, then show auth prompt for Google users
  const handleDeleteAccountClick = () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }
    
    if (isEmailPasswordUser) {
      if (!deletePassword) {
        setDeleteError('Please enter your password to confirm deletion');
        return;
      }
      // Email users: proceed directly
      executeAccountDeletion();
    } else {
      // Google users: show auth prompt first
      setShowAuthPrompt(true);
    }
  };
  
  // Actually execute the account deletion
  const executeAccountDeletion = async () => {
    setShowAuthPrompt(false);
    setDeleting(true);
    setDeleteError('');
    
    try {
      const currentUser = auth.currentUser;
      const userId = currentUser.uid;
      
      // Re-authenticate based on auth provider
      if (isEmailPasswordUser) {
        const credential = EmailAuthProvider.credential(currentUser.email, deletePassword);
        await reauthenticateWithCredential(currentUser, credential);
      } else {
        // Google OAuth users: reauthenticate with popup
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(currentUser, provider);
      }
      
      // Delete Firestore data FIRST (while still authenticated)
      await deleteDoc(doc(db, 'users', userId));
      
      // Then delete Firebase Auth user (this signs out the user)
      await deleteUser(currentUser);
      
      // Explicitly sign out to ensure all listeners are cleaned up
      try {
        await signOut(auth);
      } catch (signOutError) {
        // User is already signed out after deleteUser, ignore
      }
      
      // Force navigation to home page
      window.location.href = '/';
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setDeleteError('Password is incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        setDeleteError('Please log out and log back in, then try again');
      } else if (error.code === 'auth/popup-closed-by-user') {
        setDeleteError('Authentication cancelled. Please try again.');
      } else {
        setDeleteError(error.message);
      }
      setDeleting(false);
    }
  };
  
  // Handle Stripe checkout - opens embedded checkout modal
  // For iOS native app, uses RevenueCat instead
  const handleUpgrade = async (planId, period) => {
    if (planId === 'free' || upgrading) return;
    
    setUpgradeError('');
    setUpgrading(true);
    
    const userId = user?.userId;
    const userEmail = user?.email;
    
    if (!userId || !userEmail) {
      setUpgradeError('Please sign in to upgrade');
      setUpgrading(false);
      return;
    }
    
    // iOS native app: Use RevenueCat In-App Purchase
    if (isNativeApp) {
      try {
        if (!iOSOfferings || !iOSOfferings.availablePackages?.length) {
          setUpgradeError('Subscription products not available. Please try again later.');
          setUpgrading(false);
          return;
        }
        
        // Find the appropriate package (monthly or annual)
        const packages = iOSOfferings.availablePackages;
        const targetPackage = period === 'yearly' 
          ? packages.find(p => p.packageType === 'ANNUAL') || packages[0]
          : packages.find(p => p.packageType === 'MONTHLY') || packages[0];
        
        const result = await purchaseIOSSubscription(targetPackage);
        
        if (result.success) {
          devLog('[Settings] iOS purchase successful');
          // Tier will be updated via SubscriptionContext
        } else if (result.cancelled) {
          devLog('[Settings] iOS purchase cancelled');
        }
      } catch (error) {
        devError('[Settings] iOS purchase error:', error);
        setUpgradeError(error.message || 'Purchase failed. Please try again.');
      } finally {
        setUpgrading(false);
      }
      return;
    }
    
    // Web: Open embedded Stripe checkout modal
    setCheckoutPriceType(period);
    setShowCheckoutModal(true);
    setUpgrading(false);
  };
  
  // Handle restore purchases (iOS only)
  const handleRestorePurchases = async () => {
    if (!isNativeApp) return;
    
    setUpgrading(true);
    setUpgradeError('');
    
    try {
      const result = await restoreIOSPurchases();
      if (result.success && result.isPro) {
        devLog('[Settings] Purchases restored successfully');
      } else {
        setUpgradeError('No previous purchases found.');
      }
    } catch (error) {
      devError('[Settings] Restore error:', error);
      setUpgradeError(error.message || 'Failed to restore purchases.');
    } finally {
      setUpgrading(false);
    }
  };
  
  // Handle checkout modal close
  const handleCheckoutClose = () => {
    setShowCheckoutModal(false);
    setUpgrading(false);
  };
  
  // Handle manage subscription
  const handleManageSubscription = async () => {
    try {
      const userId = user?.userId;
      if (!userId) {
        throw new Error('Please sign in');
      }
      
      // Use Customer Center on iOS, Stripe Portal on web
      if (isNativeApp) {
        await showCustomerCenter();
      } else {
        await redirectToPortal(userId);
      }
    } catch (error) {
      devError('Subscription management error:', error);
      alert('Failed to open subscription settings: ' + error.message);
    }
  };

  // Show skeleton while data is loading
  if (dataLoading) {
    return (
      <div className="min-h-screen pb-16 px-6 pt-6" style={{ backgroundColor: 'transparent' }}>
        <SettingsPageSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: 'transparent' }}>

      <div className="max-w-3xl xl:max-w-6xl mx-auto">
        
        <PageHead title="Settings" meta="Account and preferences" />

        {/* Two columns on wide screens: preferences left, account and billing right */}
        <div className="xl:grid xl:grid-cols-2 xl:gap-x-12 xl:items-start">
          <div className="min-w-0">
            {/* Profile Section */}
            <section 
              className="pt-5 pb-2 mb-6"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <h3 
                className="text-[15px] mb-5 flex items-center gap-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                Profile
                <DevBadge />
              </h3>
          
              {/* Display Name */}
              <div className="mb-6">
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  Display Name
                </label>
                <div className="flex items-center gap-3">
                  {editingName ? (
                    <>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="flex-1 px-4 py-2.5 text-sm rounded-md outline-none transition-all focus:ring-2"
                        style={{ 
                          backgroundColor: colors.bg,
                          color: colors.textPrimary,
                          border: `1px solid ${colors.border}`
                        }}
                        autoFocus
                      />
                      <button
                        onClick={handleUpdateName}
                        disabled={savingName}
                        className="p-2.5 rounded-md transition-all "
                        style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                      >
                        {savingName ? '...' : <Check size={16} />}
                      </button>
                      <button
                        onClick={() => { setEditingName(false); setDisplayName(user?.displayName || ''); }}
                        className="p-2.5 rounded-md transition-all hover:opacity-70"
                        style={{ backgroundColor: colors.bgTertiary, color: colors.textMuted }}
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <div 
                        className="flex-1 px-4 py-2.5 rounded-md"
                        style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                      >
                        <span style={{ color: colors.textPrimary }}>{displayName || 'Not set'}</span>
                      </div>
                      <button
                        onClick={() => setEditingName(true)}
                        className="px-4 py-2.5 text-xs rounded-md transition-all font-medium"
                        style={{ color: colors.accent, border: `1px solid ${colors.accent}` }}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
                {nameError && <p className="text-xs mt-2" style={{ color: colors.error }}>{nameError}</p>}
                {nameSuccess && <p className="text-xs mt-2" style={{ color: colors.success }}>Name updated successfully!</p>}
              </div>
          
              {/* Email (read-only) */}
              <div className="mb-6">
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  Email Address
                </label>
                <div 
                  className="flex items-center gap-3 px-4 py-2.5 rounded-md"
                  style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                >
                  <Mail size={16} style={{ color: colors.textMuted }} />
                  <span style={{ color: colors.textSecondary }}>{user?.email}</span>
                </div>
              </div>
          
              {/* Password Update (only for email/password users) */}
              {isEmailPasswordUser && (
                <div>
                  <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                    Password
                  </label>
              
                  {showPasswordForm ? (
                    <div className="space-y-3">
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Current password"
                        className="w-full px-4 py-2.5 text-sm rounded-md outline-none"
                        style={{ 
                          backgroundColor: colors.bg,
                          color: colors.textPrimary,
                          border: `1px solid ${colors.border}`
                        }}
                      />
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="New password"
                        className="w-full px-4 py-2.5 text-sm rounded-md outline-none"
                        style={{ 
                          backgroundColor: colors.bg,
                          color: colors.textPrimary,
                          border: `1px solid ${colors.border}`
                        }}
                      />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="w-full px-4 py-2.5 text-sm rounded-md outline-none"
                        style={{ 
                          backgroundColor: colors.bg,
                          color: colors.textPrimary,
                          border: `1px solid ${colors.border}`
                        }}
                      />
                      {passwordError && <p className="text-xs" style={{ color: colors.error }}>{passwordError}</p>}
                      <div className="flex gap-3">
                        <button
                          onClick={handleUpdatePassword}
                          disabled={savingPassword}
                          className="px-4 py-2.5 text-xs rounded-md transition-all font-medium"
                          style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                        >
                          {savingPassword ? 'Updating...' : 'Update Password'}
                        </button>
                        <button
                          onClick={() => { setShowPasswordForm(false); setPasswordError(''); }}
                          className="px-4 py-2.5 text-xs rounded-md transition-all hover:opacity-70 font-medium"
                          style={{ color: colors.textSecondary, border: `1px solid ${colors.border}` }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div 
                        className="flex-1 flex items-center gap-3 px-4 py-2.5 rounded-md"
                        style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                      >
                        <Lock size={16} style={{ color: colors.textMuted }} />
                        <span style={{ color: colors.textSecondary }}>••••••••</span>
                      </div>
                      <button
                        onClick={() => setShowPasswordForm(true)}
                        className="px-4 py-2.5 text-xs rounded-md transition-all font-medium"
                        style={{ color: colors.accent, border: `1px solid ${colors.accent}` }}
                      >
                        Change
                      </button>
                    </div>
                  )}
                  {passwordSuccess && <p className="text-xs mt-2" style={{ color: colors.success }}>Password updated successfully!</p>}
                </div>
              )}
            </section>

            <AssistantMemory />

            {/* Notifications Section */}
            <section 
              className="pt-5 pb-2 mb-6"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <h3 
                className="text-[15px] mb-5 flex items-center gap-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                Email Notifications
              </h3>
          
              {/* Daily Digest Toggle */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock size={14} style={{ color: colors.textMuted }} />
                    <span style={{ color: colors.textPrimary, fontWeight: 500 }}>Daily Digest</span>
                  </div>
                  <p className="text-xs" style={{ color: colors.textMuted }}>
                    Receive a morning email at 8 AM with your habits, tasks, events, and goal progress
                  </p>
                </div>
                <button
                  onClick={handleDailyReminderToggle}
                  disabled={savingNotifications}
                  className="relative w-12 h-7 rounded-full transition-all duration-150"
                  style={{ 
                    backgroundColor: dailyReminderEnabled ? colors.accent : colors.bgTertiary,
                    opacity: savingNotifications ? 0.7 : 1,
                  }}
                >
                  <div 
                    className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all duration-150"
                    style={{ 
                      left: dailyReminderEnabled ? 'calc(100% - 24px)' : '4px',
                    }}
                  />
                </button>
              </div>
          
              {dailyReminderEnabled && (
                <div 
                  className="mt-4 p-4 rounded-md"
                  style={{ backgroundColor: `${colors.accent}10`, border: `1px solid ${colors.accent}30` }}
                >
                  <p className="text-sm mb-3" style={{ color: colors.textSecondary }}>
                    You'll receive daily emails at <strong style={{ color: colors.textPrimary }}>8:00 AM</strong> in your local timezone.
                  </p>
              
                  {/* Test Email Button - Only for dev */}
                  {isDev && (
                    <button
                      onClick={handleTestEmail}
                      disabled={testingEmail}
                      className="px-4 py-2 text-xs rounded-md transition-all flex items-center gap-2 font-medium"
                      style={{ 
                        color: colors.accent, 
                        border: `1px solid ${colors.accent}`,
                        opacity: testingEmail ? 0.7 : 1,
                      }}
                    >
                      {testingEmail ? (
                        <>
                          <Loader size={12} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail size={12} />
                          Send Test Email
                        </>
                      )}
                    </button>
                  )}
              
                  {emailTestResult && (
                    <p 
                      className="text-xs mt-2" 
                      style={{ color: emailTestResult.success ? colors.success : colors.error }}
                    >
                      {emailTestResult.message}
                    </p>
                  )}
                </div>
              )}
            </section>

            {/* Appearance Section */}
            <section 
              className="pt-5 pb-2 mb-6"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <h3 
                className="text-[15px] mb-5 flex items-center gap-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                Appearance
              </h3>
          
              <div className="space-y-3">
                {/* Manual Theme Toggle */}
                <button
                  onClick={() => changeThemeMode('manual')}
                  className="w-full flex items-center justify-between p-4 rounded-md transition-all "
                  style={{ 
                    backgroundColor: themeMode === 'manual' ? `${colors.accent}15` : colors.bg,
                    border: `1px solid ${themeMode === 'manual' ? colors.accent : colors.border}`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    {isDark ? <Moon size={18} style={{ color: colors.textSecondary }} /> : <Sun size={18} style={{ color: colors.warning }} />}
                    <div className="text-left">
                      <div style={{ color: colors.textPrimary, fontWeight: 500 }}>Manual</div>
                      <div className="text-xs" style={{ color: colors.textMuted }}>Toggle between light and dark mode</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {themeMode === 'manual' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleTheme(); }}
                        className="relative w-12 h-7 rounded-full transition-all duration-150"
                        style={{ backgroundColor: isDark ? colors.textSecondary : colors.warning }}
                      >
                        <div 
                          className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all duration-150 flex items-center justify-center"
                          style={{ left: isDark ? 'calc(100% - 24px)' : '4px' }}
                        >
                          {isDark ? <Moon size={10} style={{ color: colors.textSecondary }} /> : <Sun size={10} style={{ color: colors.warning }} />}
                        </div>
                      </button>
                    )}
                    {themeMode === 'manual' && <Check size={18} style={{ color: colors.accent }} />}
                  </div>
                </button>
            
                {/* System Preference */}
                <button
                  onClick={() => changeThemeMode('system')}
                  className="w-full flex items-center justify-between p-4 rounded-md transition-all "
                  style={{ 
                    backgroundColor: themeMode === 'system' ? `${colors.accent}15` : colors.bg,
                    border: `1px solid ${themeMode === 'system' ? colors.accent : colors.border}`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <Monitor size={18} style={{ color: colors.accent }} />
                    <div className="text-left">
                      <div style={{ color: colors.textPrimary, fontWeight: 500 }}>System</div>
                      <div className="text-xs" style={{ color: colors.textMuted }}>Match your device's appearance settings</div>
                    </div>
                  </div>
                  {themeMode === 'system' && <Check size={18} style={{ color: colors.accent }} />}
                </button>
            
                {/* Time-based (Sunset) */}
                <button
                  onClick={() => changeThemeMode('time')}
                  className="w-full flex items-center justify-between p-4 rounded-md transition-all "
                  style={{ 
                    backgroundColor: themeMode === 'time' ? `${colors.accent}15` : colors.bg,
                    border: `1px solid ${themeMode === 'time' ? colors.accent : colors.border}`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <Sunset size={18} style={{ color: colors.warning }} />
                    <div className="text-left">
                      <div style={{ color: colors.textPrimary, fontWeight: 500 }}>Time of Day</div>
                      <div className="text-xs" style={{ color: colors.textMuted }}>Dark mode from sunset to sunrise</div>
                    </div>
                  </div>
                  {themeMode === 'time' && <Check size={18} style={{ color: colors.accent }} />}
                </button>
              </div>
          
              {themeMode === 'time' && (
                <div 
                  className="mt-4 p-4 rounded-md"
                  style={{ backgroundColor: `${colors.warning}10`, border: `1px solid ${colors.warning}30` }}
                >
                  <p className="text-sm" style={{ color: colors.textSecondary }}>
                    Theme adjusts automatically based on approximate sunrise/sunset times for your hemisphere.
                  </p>
                </div>
              )}

              {/* Row density (this device only) */}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                <div>
                  <div style={{ color: colors.textPrimary, fontWeight: 500 }}>Row density</div>
                  <div className="text-xs" style={{ color: colors.textMuted }}>Compact fits more rows on larger screens. Saved on this device.</div>
                </div>
                <div className="inline-flex" role="radiogroup" aria-label="Row density" style={{ border: `1px solid ${colors.borderStrong}` }}>
                  {[['comfortable', 'Comfortable'], ['compact', 'Compact']].map(([value, label], i) => (
                    <button
                      key={value}
                      role="radio"
                      aria-checked={density === value}
                      onClick={() => { setDensity(value); setDensityState(value); }}
                      className="px-3 py-1.5 font-mono text-xs transition-colors"
                      style={{
                        backgroundColor: density === value ? colors.accent : 'transparent',
                        color: density === value ? colors.onAccent : colors.textPrimary,
                        borderLeft: i ? `1px solid ${colors.borderStrong}` : 'none',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Today layout (this device only) */}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                <div>
                  <div style={{ color: colors.textPrimary, fontWeight: 500 }}>Today layout</div>
                  <div className="text-xs" style={{ color: colors.textMuted }}>One screen fits the whole day in the window. Saved on this device.</div>
                </div>
                <div className="inline-flex" role="radiogroup" aria-label="Today layout" style={{ border: `1px solid ${colors.borderStrong}` }}>
                  {[['screen', 'One screen'], ['full', 'Full page']].map(([value, label], i) => (
                    <button
                      key={value}
                      role="radio"
                      aria-checked={todayLayout === value}
                      onClick={() => { setTodayLayout(value); setTodayLayoutState(value); }}
                      className="px-3 py-1.5 font-mono text-xs transition-colors"
                      style={{
                        backgroundColor: todayLayout === value ? colors.accent : 'transparent',
                        color: todayLayout === value ? colors.onAccent : colors.textPrimary,
                        borderLeft: i ? `1px solid ${colors.borderStrong}` : 'none',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Achievements Section - Pro Feature */}
            <section
              className="pt-5 pb-2 mb-6"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-lg flex items-center gap-2 font-semibold tracking-tight"
                  style={{ color: colors.textPrimary }}
                >
                  <Trophy size={18} style={{ color: colors.warning }} />
                  Achievements
                  {!hasAnalyticsAccess && (
                    <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>PRO</span>
                  )}
                </h3>
                {hasAnalyticsAccess && (
                  <span className="text-sm" style={{ color: colors.textMuted }}>
                    {unlockedAchievements.length}/{ACHIEVEMENTS.length} Unlocked
                  </span>
                )}
              </div>

              {hasAnalyticsAccess ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {ACHIEVEMENTS.map(achievement => {
                    const isUnlocked = unlockedAchievements.includes(achievement);
                    const Icon = achievement.icon;

                    return (
                      <div
                        key={achievement.id}
                        className={`relative p-3 rounded-md text-center transition-all ${
                          isUnlocked ? '' : ''
                        }`}
                        style={{
                          backgroundColor: isUnlocked
                            ? `${colors.accent}15`
                            : `${colors.bg}80`,
                          border: `1px solid ${isUnlocked ? `${colors.accent}40` : colors.border}`,
                          opacity: isUnlocked ? 1 : 0.5,
                        }}
                      >
                        <div
                          className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center"
                          style={{
                            backgroundColor: isUnlocked ? `${colors.accent}30` : colors.bgTertiary,
                          }}
                        >
                          {isUnlocked ? (
                            <Icon size={20} style={{ color: colors.accent }} />
                          ) : (
                            <Lock size={16} style={{ color: colors.textMuted }} />
                          )}
                        </div>
                        <h4
                          className="text-xs font-medium mb-0.5 truncate"
                          style={{ color: isUnlocked ? colors.textPrimary : colors.textMuted }}
                        >
                          {achievement.name}
                        </h4>
                        <p
                          className="text-[10px] leading-tight"
                          style={{ color: colors.textMuted }}
                        >
                          {achievement.description}
                        </p>
                        {isUnlocked && (
                          <div
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: colors.accent }}
                          >
                            <Check size={10} style={{ color: colors.onAccent }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center"
                    style={{ backgroundColor: `${colors.warning}20` }}
                  >
                    <Trophy size={28} style={{ color: colors.warning }} />
                  </div>
                  <p className="text-sm mb-1" style={{ color: colors.textPrimary }}>
                    Unlock Achievements
                  </p>
                  <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
                    Earn badges for your habit consistency, task completion, and goal progress
                  </p>
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="text-sm font-medium px-4 py-2 rounded-md transition-all "
                    style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                  >
                    Upgrade to Pro
                  </button>
                </div>
              )}
            </section>

            {/* Data Management */}
            <section 
              className="pt-5 pb-2"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <h3 
                className="text-lg mb-4 flex items-center gap-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                <Shield size={18} style={{ color: colors.accent }} />
                Data Management
              </h3>
          
              <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
                Download a copy of all your data including habits, tasks, goals, events, and completion history.
              </p>
          
              <button
                onClick={handleExportData}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2.5 text-xs rounded-md transition-all disabled:opacity-50 font-medium"
                style={{ 
                  backgroundColor: colors.accent, 
                  color: colors.onAccent 
                }}
              >
                {exporting ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={14} />
                    Export My Data
                  </>
                )}
              </button>
            </section>
          </div>
          <div className="min-w-0">
            {/* Subscription Section */}
            <section 
              className="pt-5 pb-2 mb-6"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-2">
                <h3 
                  className="text-lg flex items-center gap-2 flex-wrap font-semibold tracking-tight"
                  style={{ color: colors.textPrimary }}
                >
                  {isDev ? <Shield size={18} style={{ color: colors.error }} /> : 
                   isFriend ? <Heart size={18} style={{ color: colors.warning }} /> :
                   <Crown size={18} style={{ color: colors.warning }} />}
                  Subscription
                  {isDev && (
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${colors.error}20`, color: colors.error }}
                    >
                      Developer Access
                    </span>
                  )}
                  {isFriend && (
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${colors.warning}20`, color: colors.warning }}
                    >
                      Friend of mind.
                    </span>
                  )}
                  {isPro && !isDev && !isFriend && (
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}
                    >
                      Pro Active
                    </span>
                )}
                </h3>
            
                {/* Billing Period Toggle - hide for dev users, friend users, and Pro users */}
                {!isDev && !isFriend && !isPro && (
                  <div className="relative self-start md:self-auto">
                    <div 
                      className="flex rounded-md overflow-hidden"
                      style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                    >
                      <button
                        onClick={() => setBillingPeriod('monthly')}
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all"
                        style={{
                          backgroundColor: billingPeriod === 'monthly' ? colors.accent : 'transparent',
                          color: billingPeriod === 'monthly' ? colors.onAccent : colors.textMuted
                        }}
                      >
                        Monthly
                      </button>
                      <button
                        onClick={() => setBillingPeriod('yearly')}
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all"
                        style={{
                          backgroundColor: billingPeriod === 'yearly' ? colors.accent : 'transparent',
                          color: billingPeriod === 'yearly' ? colors.onAccent : colors.textMuted
                        }}
                      >
                        Yearly
                      </button>
                    </div>
                    <span 
                      className="absolute -top-2.5 -right-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full whitespace-nowrap"
                      style={{ backgroundColor: colors.success, color: colors.onAccent }}
                    >
                      -17%
                    </span>
                  </div>
                )}
              </div>
          
              {/* Dev Tier Message */}
              {isDev && (
                <div 
                  className="mb-6 p-4 rounded-md flex items-center gap-3"
                  style={{ backgroundColor: `${colors.error}10`, border: `1px solid ${colors.error}30` }}
                >
                  <Shield size={24} style={{ color: colors.error }} />
                  <div>
                    <p className="font-medium" style={{ color: colors.textPrimary }}>Developer Account</p>
                    <p className="text-sm" style={{ color: colors.textMuted }}>
                      You have full access to all features as a developer. No subscription required.
                    </p>
                  </div>
                </div>
              )}
          
              {/* Friend of mind. Celebration Section */}
              {isFriend && (
                <div 
                  className="rounded-lg p-6 text-center relative overflow-hidden"
                  style={{ 
                    backgroundColor: colors.bg,
                    border: `1px solid ${colors.border}`,
                  }}
                >
              
                  <div className="relative z-10">
                    {/* Header with icon */}
                    <div className="flex justify-center mb-4">
                      <Heart size={24} strokeWidth={1.5} style={{ color: colors.textSecondary }} />
                    </div>
                
                    <h4 
                      className="text-2xl mb-2"
                      style={{ color: colors.textPrimary, fontWeight: 600, letterSpacing: '-0.035em' }}
                    >
                      Friend of mind.
                    </h4>
                
                    <p className="text-sm mb-2" style={{ color: colors.textSecondary }}>
                      You're part of the mind. family.
                    </p>
                
                    <p className="text-xs mb-6" style={{ color: colors.textMuted }}>
                      Thank you for believing in us and supporting our journey. Your encouragement means everything. This one's on us, with love.
                    </p>
                
                    {/* Features Grid */}
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6 text-left">
                      {[
                        { title: 'Unlimited Habits', desc: 'Track as many as you want' },
                        { title: 'Goals & Milestones', desc: 'Set and crush your goals' },
                        { title: 'Task Management', desc: 'Organize your daily tasks' },
                        { title: 'Calendar & Events', desc: 'Plan your schedule' },
                        { title: 'Google Calendar', desc: 'Sync seamlessly' },
                        { title: 'Custom Themes', desc: 'Make it yours' },
                        { title: 'Advanced Analytics', desc: 'Deep insights' },
                        { title: 'Priority Support', desc: 'We\'ve got your back' },
                        { title: 'Forever Free', desc: 'Our gift to you' },
                      ].map((feature, idx) => (
                        <li key={idx} className="flex gap-2">
                          <Check size={14} className="mt-0.5 flex-shrink-0" style={{ color: colors.accent }} />
                          <div>
                          <p className="text-sm" style={{ color: colors.textPrimary }}>{feature.title}</p>
                          <p className="text-xs" style={{ color: colors.textMuted }}>{feature.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                
                    {/* Gratitude message */}
                    <p className="text-xs" style={{ color: colors.textMuted }}>All features unlocked, with gratitude</p>
                  </div>
                </div>
              )}
          
              {/* Plans Grid - hide for dev users and friends, show Pro celebration for Pro users */}
              {!isDev && !isFriend && (
                isPro ? (
                  /* Pro User Celebration Section */
                  <div 
                    className="rounded-lg p-6 text-center relative overflow-hidden"
                    style={{ 
                      backgroundColor: colors.bg,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                
                    <div className="relative z-10">
                      {/* Header with icon */}
                      <div className="flex justify-center mb-4">
                        <Check size={24} strokeWidth={1.5} style={{ color: colors.accent }} />
                      </div>
                  
                      <h4 
                        className="text-2xl mb-2 font-semibold tracking-[-0.035em]"
                        style={{ color: colors.textPrimary, }}
                      >
                        You're a Pro member
                      </h4>
                  
                      <p className="text-sm mb-6" style={{ color: colors.textMuted }}>
                        Thank you for supporting mind. You have access to everything.
                      </p>
                  
                      {/* Pro Features Grid */}
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6 text-left">
                        {[
                          { title: 'Unlimited Habits', desc: 'Track as many as you want' },
                          { title: 'Goals & Milestones', desc: 'Set and crush your goals' },
                          { title: 'Task Management', desc: 'Organize your daily tasks' },
                          { title: 'Calendar & Events', desc: 'Plan your schedule' },
                          { title: 'Google Calendar', desc: 'Sync seamlessly' },
                          { title: 'Custom Themes', desc: 'Make it yours' },
                          { title: 'Advanced Analytics', desc: 'Deep insights' },
                          { title: 'Priority Support', desc: 'We\'ve got your back' },
                          { title: 'Early Access', desc: 'New features first' },
                        ].map((feature, idx) => (
                          <li key={idx} className="flex gap-2">
                            <Check size={14} className="mt-0.5 flex-shrink-0" style={{ color: colors.accent }} />
                            <div>
                            <p className="text-sm" style={{ color: colors.textPrimary }}>{feature.title}</p>
                            <p className="text-xs" style={{ color: colors.textMuted }}>{feature.desc}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                  
                      {/* Thank you message */}
                      <p className="text-xs" style={{ color: colors.textMuted }}>All features unlocked</p>
                    </div>
                  </div>
                ) : (
                  /* Free User - Show comparison grid */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {PLANS.map((plan) => {
                      const isCurrentPlan = plan.id === 'free';
                      const price = plan.id === 'free' ? 0 : (billingPeriod === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice);
                  
                      return (
                        <div
                          key={plan.id}
                          className={`rounded-lg p-5 transition-all ${plan.popular ? 'ring-2' : ''}`}
                          style={{ 
                            backgroundColor: colors.bg,
                            border: `1px solid ${plan.popular ? colors.accent : colors.border}`,
                            ringColor: colors.accent
                          }}
                        >
                          {/* Plan Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h4 
                                className="text-lg font-medium flex items-center gap-2"
                                style={{ color: colors.textPrimary }}
                              >
                                {plan.name}
                                {plan.popular && (
                                  <span 
                                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                                    style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                                  >
                                    Popular
                                  </span>
                                )}
                              </h4>
                              <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                                {plan.description}
                              </p>
                            </div>
                            {plan.id === 'pro' && (
                              <Sparkles size={24} style={{ color: colors.warning }} />
                            )}
                          </div>
                      
                          {/* Price */}
                          <div className="mb-4">
                            <span className="text-3xl font-bold" style={{ color: colors.textPrimary }}>
                              ${price}
                            </span>
                            {plan.id === 'pro' && (
                              <span className="text-sm ml-1" style={{ color: colors.textMuted }}>
                                /{billingPeriod === 'yearly' ? 'year' : 'month'}
                              </span>
                            )}
                          </div>
                      
                          {/* Features */}
                          <ul className="space-y-2 mb-5">
                            {plan.features.map((feature, idx) => (
                              <li 
                                key={idx}
                                className="flex items-center gap-2 text-sm"
                                style={{ color: feature.included ? colors.textSecondary : colors.textMuted }}
                              >
                                {feature.included ? (
                                  <Check size={14} style={{ color: colors.success }} />
                                ) : (
                                  <X size={14} style={{ color: colors.textMuted }} />
                                )}
                                <span className={feature.included ? '' : 'line-through opacity-50'}>
                                  {feature.text}
                                </span>
                              </li>
                            ))}
                          </ul>
                      
                          {/* CTA Button */}
                          <button
                            onClick={() => !isCurrentPlan && !upgrading && handleUpgrade(plan.id, billingPeriod)}
                            disabled={isCurrentPlan || upgrading}
                            className="w-full py-3 text-xs rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                            style={{ 
                              backgroundColor: isCurrentPlan ? colors.bgTertiary : (plan.popular ? colors.accent : 'transparent'),
                              color: isCurrentPlan ? colors.textMuted : (plan.popular ? colors.onAccent : colors.accent),
                              border: plan.popular ? 'none' : `1px solid ${colors.accent}`
                            }}
                          >
                            {upgrading && plan.id === 'pro' ? (
                              <>
                                <Loader size={14} className="animate-spin" />
                                <span>Processing...</span>
                              </>
                            ) : (
                              isCurrentPlan ? 'Current Plan' : plan.cta
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
          
              {/* Upgrade Error */}
              {upgradeError && (
                <p className="text-center mt-4 text-sm" style={{ color: colors.error }}>
                  {upgradeError}
                </p>
              )}
          
              {/* Restore Purchases - iOS only */}
              {isNativeApp && !isPro && !isDev && !isFriend && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleRestorePurchases}
                    disabled={upgrading}
                    className="text-sm underline transition-all hover:opacity-70"
                    style={{ color: colors.textMuted }}
                  >
                    {upgrading ? 'Restoring...' : 'Restore Purchases'}
                  </button>
                </div>
              )}
            </section>

            {/* Billing Section - hide for dev users and friend tier */}
            {!isDev && !isFriend && (
            <section 
              className="pt-5 pb-2 mb-6"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <h3 
                className="text-[15px] mb-5 flex items-center gap-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                Billing
              </h3>
          
              {!isPro ? (
                <div 
                  className="text-center py-8 rounded-md"
                  style={{ backgroundColor: colors.bg, border: `1px dashed ${colors.border}` }}
                >
                  <CreditCard size={32} className="mx-auto mb-3 opacity-30" style={{ color: colors.textMuted }} />
                  <p style={{ color: colors.textMuted }} className="text-sm mb-4">
                    No billing information on file
                  </p>
                  <p style={{ color: colors.textSecondary }} className="text-xs">
                    Upgrade to Pro to add a payment method
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Subscription Status */}
                  <div 
                    className="p-4 rounded-md"
                    style={{ 
                      backgroundColor: colors.bg,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-xs font-medium" style={{ color: colors.textMuted }}>
                          Current Plan
                        </p>
                        <p className="text-lg font-medium mt-1 flex items-center gap-2" style={{ color: colors.textPrimary }}>
                          <Sparkles size={16} style={{ color: colors.accent }} />
                          mind. Pro
                        </p>
                      </div>
                      <div 
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ 
                          backgroundColor: subscriptionData?.subscriptionStatus === 'active' 
                            ? `${colors.success}15` 
                            : `${colors.warning}15`,
                          color: subscriptionData?.subscriptionStatus === 'active' 
                            ? colors.success 
                            : colors.warning,
                          border: `1px solid ${subscriptionData?.subscriptionStatus === 'active' ? colors.success : colors.warning}30`
                        }}
                      >
                        {subscriptionData?.subscriptionStatus === 'active' ? 'Active' : 
                         subscriptionData?.subscriptionStatus === 'canceled' ? 'Canceling' : 'Active'}
                      </div>
                    </div>
                
                    <div className="grid grid-cols-2 gap-4 mt-4 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                      <div>
                        <p className="text-xs" style={{ color: colors.textMuted }}>Plan</p>
                        <p className="text-sm font-medium mt-1" style={{ color: colors.textPrimary }}>
                          {subscriptionData?.priceType === 'yearly' ? '$50.00/year' : '$5.00/month'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: colors.textMuted }}>
                          {subscriptionData?.subscriptionStatus === 'canceled' ? 'Access Until' : 'Next Billing Date'}
                        </p>
                        <p className="text-sm font-medium mt-1" style={{ color: colors.textPrimary }}>
                          {subscriptionData?.subscriptionEnd 
                            ? (subscriptionData.subscriptionEnd.toDate 
                                ? subscriptionData.subscriptionEnd.toDate() 
                                : new Date(subscriptionData.subscriptionEnd)
                              ).toLocaleDateString('en-US', { 
                                month: 'long', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Payment Method - Link to Stripe */}
                  <div 
                    className="p-4 rounded-md"
                    style={{ 
                      backgroundColor: colors.bg,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-8 rounded flex items-center justify-center"
                          style={{ backgroundColor: colors.bgTertiary }}
                        >
                          <CreditCard size={18} style={{ color: colors.textMuted }} />
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Payment Method</p>
                          <p className="text-sm font-medium" style={{ color: colors.textPrimary }}>
                            Managed by Stripe
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                            View and update in billing portal
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleManageSubscription}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-all hover:opacity-70"
                        style={{ 
                          backgroundColor: `${colors.accent}15`,
                          color: colors.accent,
                          border: `1px solid ${colors.accent}30`
                        }}
                      >
                        Manage
                        <ExternalLink size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
            )}

            {/* Admin Section - Dev Only */}
            {isDev && (
              <section 
                className="rounded-lg p-6 mb-6"
                style={{ 
                  backgroundColor: colors.bgSecondary, 
                  border: `1px solid ${colors.success}30`,
              
                }}
              >
                <h3 
                  className="text-lg mb-4 flex items-center gap-2 font-semibold tracking-tight"
                  style={{ color: colors.success }}
                >
                  <Shield size={18} />
                  Developer Tools
                </h3>
            
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full flex items-center justify-between p-4 rounded-md transition-all hover:opacity-80"
                  style={{ 
                    backgroundColor: colors.bg,
                    border: `1px solid ${colors.border}`
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${colors.success}20` }}
                    >
                      <Settings size={18} style={{ color: colors.success }} />
                    </div>
                    <div className="text-left">
                      <p style={{ color: colors.textPrimary }} className="font-medium">
                        Admin Panel
                      </p>
                      <p style={{ color: colors.textMuted }} className="text-xs">
                        Manage user tiers and access levels
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={18} style={{ color: colors.textMuted }} />
                </button>
              </section>
            )}

            {/* Logout Section - visible on mobile */}
            <section 
              className="pt-5 pb-2 mb-6 md:hidden"
              style={{ borderTop: `1px solid ${colors.textPrimary}` }}
            >
              <h3 
                className="text-lg mb-4 flex items-center gap-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                <LogOut size={18} style={{ color: colors.accent }} />
                Account
              </h3>
              <button
                onClick={async () => {
                  try {
                    await signOut(auth);
                    navigate('/');
                  } catch (error) {
                    devError('Logout error:', error);
                  }
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md transition-all active:scale-95"
                style={{ 
                  backgroundColor: colors.bg, 
                  border: `1px solid ${colors.border}`,
                  color: colors.textPrimary 
                }}
              >
                <LogOut size={18} style={{ color: colors.error }} />
                <span>Log Out</span>
              </button>
            </section>

            {/* Danger Zone */}
            <section 
              className="rounded-lg p-6"
              style={{ 
                backgroundColor: colors.bgSecondary, 
                border: `1px solid ${colors.error}30`,
            
              }}
            >
              <h3 
                className="text-lg mb-4 flex items-center gap-2 font-semibold tracking-tight"
                style={{ color: colors.error }}
              >
                <AlertTriangle size={18} />
                Danger Zone
              </h3>
          
              {/* Check for active subscription that hasn't been cancelled */}
              {(() => {
                // User has an active subscription that they HAVE NOT cancelled yet
                // subscriptionStatus = 'active' AND cancelAtPeriodEnd is NOT true
                const hasUncancelledSubscription = subscriptionData?.subscriptionStatus === 'active' && 
                  !subscriptionData?.cancelAtPeriodEnd;
            
                // Handler to sync subscription status from Stripe
                const handleSyncSubscription = async () => {
                  setSyncing(true);
                  setSyncMessage('');
                  try {
                    const result = await syncSubscriptionStatus(user.userId);
                    devLog('✅ Sync result:', result);
                    setSyncMessage(`Synced! Status: ${result.subscriptionStatus}, Cancelled: ${result.cancelAtPeriodEnd}`);
                    // Reload the page to refresh subscription data
                    setTimeout(() => window.location.reload(), 1500);
                  } catch (error) {
                    devError('Sync error:', error);
                    setSyncMessage('Sync failed: ' + error.message);
                  } finally {
                    setSyncing(false);
                  }
                };
            
                if (hasUncancelledSubscription) {
                  return (
                    <div 
                      className="p-4 rounded-md"
                      style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${colors.error}15` }}
                        >
                          <CreditCard size={18} style={{ color: colors.error }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium mb-1" style={{ color: colors.textPrimary }}>
                            Active Subscription
                          </p>
                          <p className="text-sm mb-3" style={{ color: colors.textMuted }}>
                            You cannot delete your account while you have an active subscription. Please cancel your subscription first through the billing portal.
                          </p>
                          <button
                            onClick={handleSyncSubscription}
                            disabled={syncing}
                            className="flex items-center gap-1.5 text-xs transition-all hover:opacity-70 disabled:opacity-50"
                            style={{ color: colors.textMuted }}
                          >
                            <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} />
                            {syncing ? 'Syncing...' : 'Already cancelled? Sync'}
                          </button>
                          {syncMessage && (
                            <p className="text-xs mt-2" style={{ color: syncMessage.includes('failed') ? colors.error : colors.success }}>
                              {syncMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
            
                return (
                  <>
                    <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
                      Once you delete your account, there is no going back. All your data will be permanently deleted.
                    </p>
                
                    {showDeleteConfirm ? (
                      <div 
                        className="p-4 rounded-md space-y-4"
                        style={{ backgroundColor: colors.bg, border: `1px solid ${colors.error}30` }}
                      >
                        <p className="text-sm font-medium" style={{ color: colors.error }}>
                          Are you absolutely sure?
                        </p>
                    
                        {isEmailPasswordUser && (
                          <input
                            type="password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            placeholder="Enter your password to confirm"
                            className="w-full px-4 py-2.5 text-sm rounded-md outline-none"
                            style={{ 
                              backgroundColor: colors.bgSecondary,
                              color: colors.textPrimary,
                              border: `1px solid ${colors.border}`
                            }}
                          />
                        )}
                    
                        <div>
                          <label className="block text-xs mb-2" style={{ color: colors.textMuted }}>
                            Type <strong>DELETE</strong> to confirm
                          </label>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="DELETE"
                            className="w-full px-4 py-2.5 text-sm rounded-md outline-none"
                            style={{ 
                              backgroundColor: colors.bgSecondary,
                              color: colors.textPrimary,
                              border: `1px solid ${colors.border}`
                            }}
                          />
                        </div>
                    
                        {deleteError && <p className="text-xs" style={{ color: colors.error }}>{deleteError}</p>}
                    
                        <div className="flex gap-3">
                          <button
                            onClick={handleDeleteAccountClick}
                            disabled={deleting}
                            className="flex-1 py-2.5 text-xs rounded-md transition-all font-medium"
                            style={{ backgroundColor: colors.error, color: colors.onAccent }}
                          >
                            {deleting ? 'Deleting...' : 'Delete My Account'}
                          </button>
                          <button
                            onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); setDeleteConfirmText(''); setDeletePassword(''); setShowAuthPrompt(false); }}
                            className="flex-1 py-2.5 text-xs rounded-md transition-all hover:opacity-70 font-medium"
                            style={{ color: colors.textSecondary, border: `1px solid ${colors.border}` }}
                          >
                            Cancel
                          </button>
                        </div>
                    
                        {/* Auth confirmation prompt for Google users */}
                        {showAuthPrompt && (
                          <div 
                            className="fixed inset-0 z-50 flex items-center justify-center p-4"
                            style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
                          >
                            <div 
                              className="max-w-md w-full p-6 rounded-lg space-y-4"
                              style={{ backgroundColor: colors.bg, border: `1px solid ${colors.textPrimary}` }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full" style={{ backgroundColor: `${colors.error}20` }}>
                                  <AlertTriangle size={24} style={{ color: colors.error }} />
                                </div>
                                <h3 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
                                  Confirm Account Deletion
                                </h3>
                              </div>
                          
                              <p className="text-sm" style={{ color: colors.textSecondary }}>
                                To delete your account, you'll need to verify your identity. A Google sign-in popup will appear to confirm it's really you.
                              </p>
                          
                              <p className="text-sm font-medium" style={{ color: colors.error }}>
                                After verification, your account and ALL data will be permanently deleted. This cannot be undone.
                              </p>
                          
                              <div className="flex gap-3 pt-2">
                                <button
                                  onClick={executeAccountDeletion}
                                  className="flex-1 py-3 text-sm font-medium rounded-md transition-all "
                                  style={{ backgroundColor: colors.error, color: colors.onAccent }}
                                >
                                  Proceed with Deletion
                                </button>
                                <button
                                  onClick={() => setShowAuthPrompt(false)}
                                  className="flex-1 py-3 text-sm font-medium rounded-md transition-all hover:opacity-70"
                                  style={{ color: colors.textSecondary, border: `1px solid ${colors.border}` }}
                                >
                                  Go Back
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                    
                        <p className="text-xs mt-3" style={{ color: colors.textMuted }}>
                          This will permanently delete all your data including habits, tasks, goals, events, and any associated history. This action cannot be undone.
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-2 px-4 py-2.5 text-xs rounded-md transition-all font-medium"
                        style={{ 
                          color: colors.error, 
                          border: `1px solid ${colors.error}` 
                        }}
                      >
                        <Trash2 size={14} />
                        Delete Account
                      </button>
                    )}
                  </>
                );
              })()}
            </section>
          </div>
        </div>
      </div>
      
      {/* Embedded Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={handleCheckoutClose}
        priceType={checkoutPriceType}
        userId={user?.userId}
        userEmail={user?.email}
      />
      
      {/* Footer */}
      <footer 
        className="px-6 py-12 mt-8"
      >
        <div className="max-w-4xl mx-auto text-center">
          <Logo size="sm" isDark={isDark} className="mx-auto mb-4" />
          <p className="text-sm mb-6" style={{ color: colors.textMuted }}>
            Build better habits. Achieve your goals.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <Link 
              to="/about" 
              className="hover:opacity-70 transition-opacity"
              style={{ color: colors.textSecondary }}
            >
              About
            </Link>
            <Link 
              to="/privacy" 
              className="hover:opacity-70 transition-opacity"
              style={{ color: colors.textSecondary }}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              className="hover:opacity-70 transition-opacity"
              style={{ color: colors.textSecondary }}
            >
              Terms of Service
            </Link>
          </div>
          <p className="mt-8 text-xs" style={{ color: colors.textMuted }}>
            © {new Date().getFullYear()} Mill Creek Media LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SettingsPage;
